/* eslint-disable react-hooks/exhaustive-deps */
// Services.js - Enhanced Version with Dynamic Data
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Css/Services.css';
import {API_URL} from '../utils/Function';

const Services = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/service.php/get`);
        const data = await response.json();
        
        if (data.success) {
          setServices(data.services);
        } else {
          setError(data.error || 'Failed to fetch services');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Truncate description to 2 lines and add "View More" link
  const truncateDescription = (description, maxLength = 150) => {
    if (!description) return '';
    
    if (description.length <= maxLength) {
      return description;
    }
    
    const truncated = description.substring(0, maxLength);
    // Find the last space to avoid cutting words
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            
            // Enhanced staggered animation with smoother timing
            cardRefs.current.forEach((card, index) => {
              if (card) {
                setTimeout(() => {
                  card.classList.add('visible');
                }, index * 150); // Increased delay for better visual flow
              }
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -30px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [services]); // Re-run when services change

  const addToRefs = (el) => {
    if (el && !cardRefs.current.includes(el)) {
      cardRefs.current.push(el);
    }
  };

  // Handle view more click - navigate to service details using slug
  const handleViewMore = (slug) => {
    if (slug) {
      navigate(`/services/${slug}`);
    } else {
      console.error('Slug is undefined for service');
    }
  };

  if (loading) {
    return (
      <section className="services-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading services...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="services-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <div className="alert alert-danger" role="alert">
                Error loading services: {error}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="services-section py-5" ref={sectionRef}>
      {/* Floating background shapes */}
      <div className="floating-shapes">
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
      </div>
      
      <div className="container">
        <div className="row">
          <div className="col-12 text-center">
            <h2 className={`services-title ${isVisible ? 'visible' : ''}`}>
              Our <span style={{borderBottom:"5px solid #9374FF"}}>Services</span>
            </h2>
            <p className={`services-subtitle ${isVisible ? 'visible' : ''}`}>
              We have assisted companies in delivering vital projects utilizing web, mobile, cloud, e-commerce and enterprise technologies, through both on-site and off-site support
            </p>
            <div className={`title-divider ${isVisible ? 'visible' : ''}`}></div>
          </div>
        </div>
        
        <div className="row">
          {services.map((service, index) => (
            <div key={service.id} className="col-lg-4 col-md-6 mb-4">
              <div 
                className="service-card"
                ref={addToRefs}
                style={{
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <div className="service-content">
                  {service.featured_image_url && (
                    <div className="service-image-container">
                      <img 
                        src={`${API_URL}/${service.featured_image_url}`} 
                        alt={service.title} 
                        className="service-image my-2"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                  <h3 className="service-title">{service.title}</h3>
                  <div className="service-divider"></div>
                  <p 
                    className="service-description"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '3em', // Ensure consistent height for 2 lines
                      lineHeight: '1.5em'
                    }}
                  >
                    {truncateDescription(service.description || service.short_description)}
                  </p>
                  <div className="text-center mt-3">
                    <button 
                      className="view-more-link"
                      onClick={() => handleViewMore(service.slug)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      View More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {services.length === 0 && !loading && (
          <div className="row">
            <div className="col-12 text-center">
              <p className="text-muted">No services available at the moment.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;